import * as React from "react";
import { observer } from "mobx-react";
import { WizardPackagePWAViewProps, LobCombinations, PackageHiddenSearchDataProps } from "../typings";
import { FlightHotel } from "../components/FlightHotel";
import { FlightHotelCar } from "../components/FlightHotelCar";
import { FlightCar } from "../components/FlightCar";
import { HotelCar } from "../components/HotelCar";
import { PackageHiddenSearchData } from "../components/PackageHiddenSearchData";
import { SubmitRow } from "src/components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { WizardPackagePWA } from "../WizardPackagePWA";
import {
  UitkLayoutGrid,
  UitkLayoutConditionalGridSpan,
  UitkLayoutConditionalGridColumnRow,
} from "uitk-react-layout-grid";
import { UitkSpacing, UitkSpacingProps } from "uitk-react-spacing";
import { PackageErrorState } from "./PackageErrorState";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";
import { MenuFields } from "./MenuFields";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { ValuePropSubtitle } from "./ValuePropSubtitle";
import { PackagesStandaloneWizardModulesContext } from "src/components/flexComponents/PageLevelProviders/WizardContextProvider/PackagesStandaloneWizardModulesContext";
import { withTypeMenu } from "./PackageTypeMenu";
import { TypeMenuPills } from "./TypeMenuPills";
import { withLocalization } from "bernie-l10n";
import { TypeMenuCheckboxes } from "./TypeMenuCheckboxes";
import { MultiLOBPackageWizardContext } from "src/components/flexComponents/PageLevelProviders/WizardContextProvider/MultiLOBPackageWizardContext";
import { Experiment as ExperimentList, isVariantEnabled } from "src/components/utility/experiment";
import { PackagesSubLobs } from "src/stores/wizard/state/typings";
import { VendorCodes } from "../../WizardCarPWA/components/vendorOptions/VendorCodes";

/**
 * This implementation is exclusive por packages.
 * Do not repeat somewhere else.
 */
const TypeMenuWithPills = withLocalization(withTypeMenu(TypeMenuPills));
const TypeMenuWithCheckboxes = withLocalization(withTypeMenu(TypeMenuCheckboxes));

export const PackageForm: React.FunctionComponent<WizardPackagePWAViewProps> = observer((props) => {
  const { context, wizardState, defaultPackageType } = props;
  const {
    packageWizardState,
    packageWizardState: { config: packageConfig },
    globalWizardState: { config: globalConfig },
    carWizardState,
    carWizardState: { config: carConfig },
    hotelWizardState: { config: hotelConfig },
  } = wizardState;

  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, medium: 12, large: 12 };
  const couponColSpan: UitkLayoutConditionalGridSpan = { small: 2, medium: 4 };

  const packageHiddenSearchData: PackageHiddenSearchDataProps = {
    pwaSubLOB: packageWizardState.subLOBState === "" ? props.defaultPackageType : packageWizardState.subLOBState,
    leg1: WizardPackagePWA.getFlightOutLegURLFragment(packageWizardState, packageConfig),
    leg2: packageWizardState.isRoundtripFlight()
      ? WizardPackagePWA.getFlightInLegURLFragment(packageWizardState, packageConfig)
      : undefined,
    flightClass: packageWizardState.flightClassCode,
    flightAirline: packageWizardState.flightAirline,
    config: packageConfig,
    packageWizardState,
    hotelConfig,
  };

  const isNewPackageOnewayFlightType =
    packageWizardState.isDesktop && packageWizardState.experimentNewPackageOnewayFlightType.variant1;
  const isNewPackageMulticityFlightType =
    packageWizardState.isDesktop && packageWizardState.experimentNewPackageMulticityFlightType.variant1;
  const experimentNewPackageVariant1 = isNewPackageOnewayFlightType || isNewPackageMulticityFlightType;
  const displayNewPackageFlightType = packageWizardState.subLOBState === "" && experimentNewPackageVariant1;

  const [lobsMissing, setLobsMissing] = React.useState(0);

  /**
   * Values used if no rehydratation(JS disabled)
   */
  const { noRehydratationValues } = packageConfig.travelers;
  const {
    isFlightCarFormValid,
    isFlightHotelDefaultFormValid,
    isFlightHotelCarFormValid,
    isFlightHotelMultiFormValid,
    isFlightHotelOneWayFormValid,
    isHotelCarFormValid,
    numberOfErrors,
    moreThanOneError,
    errorInputRef,
    subLOBState,
    flightTypeCode,
  } = packageWizardState;

  const currentSubLOB = subLOBState || defaultPackageType;
  const errorSummaryGridColumns: UitkLayoutConditionalGridSpan = { small: 2, medium: 12 };

  const invalidPackageCombination = !Object.values(props.packageSubLOBs.get("lobsAllowed")!).some(
    (val) => val === currentSubLOB
  );
  const selectedLobs = !Object.values(PackagesSubLobs).some((val) => val === currentSubLOB);

  const formHasNoErrors =
    isFlightCarFormValid &&
    isFlightHotelCarFormValid &&
    isFlightHotelDefaultFormValid &&
    isFlightHotelMultiFormValid &&
    isFlightHotelOneWayFormValid &&
    isHotelCarFormValid;

  let lobsMissingErrorState = selectedLobs ? 2 : 0;
  lobsMissingErrorState = invalidPackageCombination && !selectedLobs ? 99 : lobsMissingErrorState;
  React.useEffect(() => setLobsMissing(lobsMissingErrorState), []);

  const showSubHeadingFromFlex = props.flexComponent.model.includePageSubHeading;

  const standaloneSubLOBS = [PackagesSubLobs.HOTELS, PackagesSubLobs.FLIGHTS, PackagesSubLobs.CARS];

  const isTypeMenuCheckboxesVariantEnabled = isVariantEnabled(
    context,
    ExperimentList.PWA_Package_Wizard_Type_Menu_Checkboxes
  );

  const TypeMenu = isTypeMenuCheckboxesVariantEnabled ? TypeMenuWithCheckboxes : TypeMenuWithPills;
  const displayVendorCodes =
    (currentSubLOB === PackagesSubLobs.HOTEL_CAR ||
      currentSubLOB === PackagesSubLobs.FLIGHT_CAR ||
      currentSubLOB === PackagesSubLobs.FLIGHT_HOTEL_CAR) &&
    !carConfig.isBiasedWizardEmpty;

  return (
    <PackagesStandaloneWizardModulesContext.Consumer>
      {(singleLOBWizards) => {
        const hasSingleWizards = singleLOBWizards.length > 0;
        const isSingleSubLOB = standaloneSubLOBS.includes(currentSubLOB as PackagesSubLobs);
        const displayStandaloneWizard = hasSingleWizards && isSingleSubLOB;
        const wizardHotel = singleLOBWizards.filter(({ key }) => key === "wizard-hotel-pwa-v2-1");
        const wizardFlight = singleLOBWizards.filter(({ key }) => key === "wizard-flight-pwa-1");
        const wizardCar = singleLOBWizards.filter(({ key }) => key === "wizard-car-pwa-1");

        const pillsClassName = currentSubLOB === "none" || isSingleSubLOB ? "all-t-padding-two" : "";
        const pillsCopyMargin: UitkSpacingProps["margin"] = invalidPackageCombination
          ? { blockstart: "four", blockend: "one" }
          : undefined;

        const pillsGridMargin: UitkSpacingProps["margin"] = { blockend: !displayStandaloneWizard ? "six" : "three" };
        const pillsGridJustifyContent = packageConfig.showAllSubLOB ? "space-between" : "end";

        return (
          <>
            <form
              noValidate={true}
              action={packageConfig.form.action}
              autoComplete="off"
              className="WizardPackagePWA"
              data-testid="wizard"
              onSubmit={packageWizardState.submit}
              id={packageConfig.elementId}
              data-fm={props.flexComponent.fmId}
              data-fm-title-id={props.flexComponent.fmTitleId}
            >
              <UitkSpacing margin={pillsGridMargin}>
                <UitkLayoutFlex justifyContent={pillsGridJustifyContent} alignItems="end">
                  {packageConfig.showAllSubLOB && (
                    <UitkLayoutFlexItem>
                      <div>
                        {packageConfig.showAllSubLOB && !showSubHeadingFromFlex && (
                          <ValuePropSubtitle config={packageConfig} margin={pillsCopyMargin} />
                        )}
                        <TypeMenu
                          context={context}
                          className={pillsClassName}
                          lobState={packageWizardState}
                          onSubLOBSelection={props.onSubLOBSelection}
                          defaultPackageType={
                            (packageWizardState.subLOBState as LobCombinations) || props.defaultPackageType
                          }
                          updateLobsMissing={setLobsMissing}
                          minimumLobsNeeded={packageConfig.minimumLOBsNeeded}
                          packageSubLOBs={props.packageSubLOBs}
                          currentSubLOB={currentSubLOB}
                          showOneway={isNewPackageOnewayFlightType}
                          showMulticity={isNewPackageMulticityFlightType}
                          onFlightTypeUpdate={props.onFlightTypeUpdate}
                          setFlightType={props.setFlightType}
                        />
                      </div>
                    </UitkLayoutFlexItem>
                  )}
                  {!invalidPackageCombination && !displayStandaloneWizard && (
                    <Viewport>
                      <ViewMedium>
                        <UitkLayoutFlexItem>
                          <div>
                            <MenuFields
                              currentSubLOB={currentSubLOB}
                              routeStore={props.routeStore}
                              wizardState={props.wizardState}
                              noRehydratationValues={noRehydratationValues}
                              displayNewPackageFlightType={displayNewPackageFlightType}
                              onFlightTypeUpdate={props.onFlightTypeUpdate}
                              activeFlightType={flightTypeCode}
                              showOneway={isNewPackageOnewayFlightType}
                              showMulticity={isNewPackageMulticityFlightType}
                              onSelectPreferredClassCode={props.onSelectPreferredClassCode}
                              onSelectPreferredAirline={props.onSelectPreferredAirline}
                            />
                          </div>
                        </UitkLayoutFlexItem>
                      </ViewMedium>
                    </Viewport>
                  )}
                </UitkLayoutFlex>
              </UitkSpacing>

              {!invalidPackageCombination && !displayStandaloneWizard && (
                <Viewport>
                  <ViewSmall>
                    <MenuFields
                      currentSubLOB={currentSubLOB}
                      routeStore={props.routeStore}
                      wizardState={props.wizardState}
                      noRehydratationValues={noRehydratationValues}
                      displayNewPackageFlightType={displayNewPackageFlightType}
                      onFlightTypeUpdate={props.onFlightTypeUpdate}
                      activeFlightType={flightTypeCode}
                      showOneway={isNewPackageOnewayFlightType}
                      showMulticity={isNewPackageMulticityFlightType}
                      onSelectPreferredClassCode={props.onSelectPreferredClassCode}
                      onSelectPreferredAirline={props.onSelectPreferredAirline}
                    />
                  </ViewSmall>
                  <ViewMedium />
                </Viewport>
              )}

              {!formHasNoErrors && !displayStandaloneWizard && (
                <UitkSpacing padding={{ blockstart: "three", blockend: "three" }}>
                  <UitkLayoutGrid columns={mainGridColumns} space="three">
                    <LobErrorSummary
                      locHeadingToken={props.getLocError!(false)!}
                      locHeadingArgs={moreThanOneError() && [numberOfErrors]}
                      locLinkToken={props.getLocError!(true)}
                      linkClickFocusId="id-roundtrip-error"
                      inputErrorRef={errorInputRef}
                      colSpan={errorSummaryGridColumns}
                      setInputsRefs={props.setInputsRefs}
                    />
                  </UitkLayoutGrid>
                </UitkSpacing>
              )}

              <UitkLayoutGrid columns={mainGridColumns} space="three">
                {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.FLIGHT_HOTEL && (
                  <FlightHotel
                    {...props}
                    packageType={PackagesSubLobs.FLIGHT_HOTEL}
                    packageConfig={packageConfig}
                    packageWizardState={packageWizardState}
                    toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                    togglePartialStay={props.togglePartialStay}
                    setFlightType={props.setFlightType}
                    setInputsRefs={props.setInputsRefs}
                    globalConfig={globalConfig}
                  />
                )}

                {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.FLIGHT_HOTEL_CAR && (
                  <FlightHotelCar
                    {...props}
                    packageType={PackagesSubLobs.FLIGHT_HOTEL_CAR}
                    packageConfig={packageConfig}
                    packageWizardState={packageWizardState}
                    toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                    togglePartialStay={props.togglePartialStay}
                    setInputsRefs={props.setInputsRefs}
                    globalConfig={globalConfig}
                  />
                )}
                {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.FLIGHT_CAR && (
                  <FlightCar
                    {...props}
                    packageType={PackagesSubLobs.FLIGHT_CAR}
                    packageConfig={packageConfig}
                    packageWizardState={packageWizardState}
                    toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                    togglePartialStay={props.togglePartialStay}
                    setInputsRefs={props.setInputsRefs}
                    globalConfig={globalConfig}
                  />
                )}
                {!invalidPackageCombination && currentSubLOB === PackagesSubLobs.HOTEL_CAR && (
                  <HotelCar
                    {...props}
                    packageType={PackagesSubLobs.HOTEL_CAR}
                    packageConfig={packageConfig}
                    packageWizardState={packageWizardState}
                    toggleStaySomewhereElse={props.toggleStaySomewhereElse}
                    togglePartialStay={props.togglePartialStay}
                    setInputsRefs={props.setInputsRefs}
                    globalConfig={globalConfig}
                    carWizardState={carWizardState}
                  />
                )}
                {invalidPackageCombination && !displayStandaloneWizard && (
                  <PackageErrorState
                    context={context}
                    lobState={packageWizardState}
                    lobsMissing={lobsMissing}
                    packageWizardState={packageWizardState}
                  />
                )}
                <PackageHiddenSearchData {...packageHiddenSearchData} />
              </UitkLayoutGrid>

              {displayVendorCodes && (
                <VendorCodes
                  carWizardState={carWizardState}
                  mainColSpan={mainGridColumns}
                  colSpan={couponColSpan}
                  globalConfig={globalConfig}
                />
              )}
              {!invalidPackageCombination && !displayStandaloneWizard && (
                <SubmitRow lobState={packageWizardState} rfrr={"SearchClick_Package"} globalConfig={globalConfig} />
              )}
            </form>

            <MultiLOBPackageWizardContext.Provider value={{ isInsideMultiLOBPackageWizard: true }}>
              {currentSubLOB === PackagesSubLobs.HOTELS && wizardHotel}

              {currentSubLOB === PackagesSubLobs.FLIGHTS && wizardFlight}

              {currentSubLOB === PackagesSubLobs.CARS && wizardCar}
            </MultiLOBPackageWizardContext.Provider>
          </>
        );
      }}
    </PackagesStandaloneWizardModulesContext.Consumer>
  );
});
